'use client';

import { Loader } from '@ophelos/ui/components/loader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ophelos/ui/components/tabs';
import { Receipt } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { DebtPageHeader } from '@/components/layout/DebtPageHeader';
import { soleCreditor } from '@/lib/creditor';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { useDebtData } from '@/contexts/DebtDataContext';
import { useTranslation } from '@/hooks/useTranslation';
import { accountCurrency, debtNameByTransaction } from '@/lib/accountPayment';
import { GroupedPaymentsList } from './_partials/_paymentHistoryPartials/GroupedPaymentsList';
import { customerTransactions } from './_partials/_shared/TransactionsList';
import { MigratedHistoryCard, migratedDebts } from './_partials/_shared/MigratedHistoryCard';

export default function PaymentHistory() {
  const { t } = useTranslation();
  const { debts, isLoading } = useDebtData();
  const { isPreviewMode } = useConfiguration();

  const transactions = customerTransactions(debts);
  const debtNames = debtNameByTransaction(debts);
  const migrated = migratedDebts(debts);
  const creditor = soleCreditor(debts);

  if (isLoading) {
    return (
      <Layout showFooter={false}>
        <Loader fullScreen />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto flex-1 h-full w-full">
        <Tabs variant="default" defaultValue="history" className="space-y-2">
          <DebtPageHeader backTo={isPreviewMode ? '/dashboard?preview=true' : '/dashboard'} creditor={creditor}>
            <TabsList className="max-w-xs w-full mx-auto max-sm:h-auto">
              <TabsTrigger value="history" className="rounded-full max-xs:text-xs">
                <Receipt className="size-4 max-xs:hidden text-muted-foreground" />
                {t('paymentHistory.title')}
              </TabsTrigger>
            </TabsList>
          </DebtPageHeader>

          <TabsContent value="history" className="max-w-lg w-full mx-auto px-4 pb-12 space-y-6">
            <GroupedPaymentsList transactions={transactions} debtNames={debtNames} currency={accountCurrency(debts)} />

            {/* Kept below and separate from the Forest list — pre-move payments are a record,
                not part of the account's payment history with us. */}
            {migrated.length > 0 && (
              <div className="space-y-4">
                {migrated.map((debt) => (
                  <MigratedHistoryCard key={debt.id} debt={debt} showDebt />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
